import React, { useCallback, useRef, useState } from "react";
import ModalWrapper from "..";
import { Button } from "../../button";
import { useTranslation } from "react-i18next";
import ScanQRCodeModal from "../ScanQRCodeModal";
import QRScanner from "qr-scanner";
import { APP_ROUTES } from "../../../routes";
import { useNavigate } from "react-router-dom";
import { CHECK_IN_OUT_TYPE_KEYS, QR_ACTIONS } from "../../../constants/common";

interface SelectCheckInCheckoutModalProps {
  isOpen: boolean;
  onOpenChange: () => void;
}

const SelectCheckInCheckoutModal = ({
  isOpen,
  onOpenChange,
}: SelectCheckInCheckoutModalProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const scannerRef = useRef<QRScanner | null>(null);

  const navigate = useNavigate();

  const { t } = useTranslation();

  const [isScanQRCodeModalOpen, setIsScanQRCodeModalOpen] =
    useState<boolean>(false);

  const stopScanning = useCallback(() => {
    if (scannerRef.current) {
      scannerRef.current.stop(); // Stop the scanner
      scannerRef.current = null; // Clear the reference
    }

    // Clean up video stream
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach((track) => track.stop());
    }
  }, []);

  const handleToggleQRCodeModal = useCallback(
    (flag: boolean) => {
      setIsScanQRCodeModalOpen(flag);
      stopScanning();
    },
    [stopScanning]
  );

  const startScanning = useCallback(
    async (type: QR_ACTIONS) => {
      try {
        handleToggleQRCodeModal(true);

        onOpenChange();

        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }

        const scanner = new QRScanner(
          videoRef.current as HTMLVideoElement,
          (result) => {
            stopScanning();

            const referenceNumber = result.split(";")[1];

            const appointmentId =
              referenceNumber.startsWith("V") || referenceNumber.startsWith("v")
                ? referenceNumber.slice(1)
                : referenceNumber;

            navigate(
              `${
                type === QR_ACTIONS.CHECK_IN
                  ? APP_ROUTES.CHECK_IN
                  : APP_ROUTES.CHECK_OUT
              }?type=${CHECK_IN_OUT_TYPE_KEYS.VEHICLE}&ID=${appointmentId}`
            );
          },
          undefined, // Error callback (optional)
          undefined, // Optional scan region calculation
          "environment" // Set preferred camera to back (environment) camera
        );

        scannerRef.current = scanner; // Store the scanner instance
        scanner.start();
      } catch (error) {
        console.error(error);
      }
    },
    [handleToggleQRCodeModal, navigate, onOpenChange, stopScanning]
  );

  return (
    <>
      <ModalWrapper isOpen={isOpen} onOpenChange={onOpenChange}>
        <div className="flex flex-col gap-5 justify-center items-center w-full">
          <p className="text-lg md:text-2xl font-medium">
            {t("whatWouldYouLikeToDo")}
          </p>

          <div className="flex gap-1 items-center">
            <Button onClick={() => startScanning(QR_ACTIONS.CHECK_IN)}>
              {t("checkIn")}
            </Button>
            <Button onClick={() => startScanning(QR_ACTIONS.CHECK_OUT)}>
              {t("checkOut")}
            </Button>
          </div>
        </div>
      </ModalWrapper>

      <ScanQRCodeModal
        isOpen={isScanQRCodeModalOpen}
        onOpenChange={() => handleToggleQRCodeModal(false)}
        videoRef={videoRef}
      />
    </>
  );
};

export default React.memo(SelectCheckInCheckoutModal);
